import { BaseWebComponentView } from '../../../../../framework/BaseWebComponentView';
import { CompanyActionContext } from '../../../types/CompanyActionContext';
import { GzTitleWebComponent } from '../../../../../ui/gzTitle';
import { GzTextWebComponent } from '../../../../../ui/gzText';
import { ImportOptionsWebComponent } from '../../importOptions';
import { GzButtonWebComponent } from '../../../../../ui/gzButton';
import { translateFn } from '../../../../../utils/translator.helper';
import { GzToastNotificationWebComponent } from '../../../../../ui/gzToastNotification';
import { ToastSeverity } from '../../../../../ui/gzToastNotification/type';
import { toggleVisibility } from '../../../../../utils/htmlElement.utils';
import { TemplateState } from '../../../../../types/common/types';
import { MdrContactDetailsFormWebComponent } from '../../mdrContactDetailsForm';
import { ContactDetailsSectionViewData } from '../types/ContactDetailsSectionViewData';
import { Subscription } from 'rxjs';
import { FormGroup } from '../../../../../ui/gzForm/formGroup/form-group';
import { MdrContactDetailsFormEvents } from '../../mdrContactDetailsForm/types';

export class ContactDetailsSectionView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  public dom: {
    $wrapper: HTMLElement;
    $sectionTitle: GzTitleWebComponent;
    $sectionInfo: GzTextWebComponent;
    $importOptions: ImportOptionsWebComponent;
    $importButton: GzButtonWebComponent;
    $contactForm: MdrContactDetailsFormWebComponent;
    $sectionFooter: GzTextWebComponent;
  };

  private importButtonSubscription?: Subscription;
  private context: CompanyActionContext;
  private isInPopUp: boolean;
  private isPartner?: boolean;

  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $sectionTitle: this.shadow.querySelector('#contact-details-section-title'),
      $sectionInfo: this.shadow.querySelector('#contact-details-section-info'),
      $importOptions: this.shadow.querySelector('#contact-details-import-options'),
      $importButton: this.shadow.querySelector('#contact-details-import-button'),
      $contactForm: this.shadow.querySelector('#contact-details-form'),
      $sectionFooter: this.shadow.querySelector('#contact-details-section-footer'),
    };

    this.injectDependenciesInChildren();
  }

  setTranslatedStrings() {
    this.dom.$sectionTitle.innerText = translateFn('Contact Details for MDR');

    if (this.context === CompanyActionContext.MyCompany) {
      this.dom.$sectionInfo.innerText = translateFn(
        'You can select an existing GravityZone user or enter a custom contact. If you select a user, the changes to the account will automatically update your contact details, ensuring they remain up to date.'
      );

      this.dom.$sectionFooter.innerText = translateFn('You can edit this information later from the My Company page.');
    } else if (this.isPartner) {
      this.dom.$sectionInfo.innerText = translateFn(
        'You can select an existing GravityZone user or enter a custom contact. If you select a user, the changes to the account will automatically update your contact details, ensuring they remain up to date.'
      );

      this.dom.$sectionFooter.innerText = translateFn(
        'You can edit this information later from the My Company page or from the Company details tab when editing a managed company.'
      );
    } else {
      this.dom.$sectionInfo.innerText = translateFn(
        'You can select an existing GravityZone user or company account or enter a custom contact. If you select a user or a company account, the changes to the account will automatically update your contact details, ensuring they remain up to date.'
      );

      this.dom.$sectionFooter.innerText = translateFn(
        'You can edit this information later from the My Company page or from the Company details tab when editing a managed company.'
      );
    }

    this.dom.$importButton.innerText = translateFn('Import to fill-in');
  }

  async buildSuccessView(viewData: ContactDetailsSectionViewData) {
    this.context = viewData.context;
    this.isInPopUp = viewData.isInPopUp;
    this.isPartner = viewData.isPartner;

    this.setTranslatedStrings();

    await this.buildImportOptions(viewData);

    if (viewData.context !== CompanyActionContext.Create && viewData.fullName && viewData.email) {
      const hasFullPhoneNumber =
        viewData.phoneNumber && viewData.phoneNumber.subscriberNumber && viewData.phoneNumber.countryCode;

      this.dom.$contactForm.viewData = {
        value: {
          fullName: viewData.fullName,
          email: viewData.email,
          emailVerificationCode: '',
          hasVerifiedEmailAddress: true,
          phoneNumber: viewData.phoneNumber,
        },
        enabled: {
          fullName: false,
          email: false,
          sendEmailVerificationCode: false,
          emailVerificationCode: false,
          phoneNumber: !hasFullPhoneNumber,
          clearData: viewData.canBeManagedByCurrentUser ?? false,
        },
      };
    }

    this.dom.$contactForm.hasStackedFormFields = viewData.hasStackedFormFields ?? false;
    this.dom.$contactForm.hasSendButtonBelowEmail = viewData.hasSendVerificationCodeButtonBelowEmail ?? false;
    this.dom.$contactForm.required = viewData.required ?? false;

    if (!this.dom.$contactForm.started) {
      await this.dom.$contactForm.start();
    } else {
      await this.dom.$contactForm.controller.render();
    }

    toggleVisibility(this.dom.$sectionFooter, this.isInPopUp);

    this.createFormGroup();
  }

  private async buildImportOptions(viewData: ContactDetailsSectionViewData): Promise<void> {
    const showImport = viewData.canBeManagedByCurrentUser ?? false;
    toggleVisibility(this.dom.$sectionInfo, showImport);
    toggleVisibility(this.dom.$importButton, showImport);
    toggleVisibility(this.dom.$importOptions, showImport);

    if (!viewData.canBeManagedByCurrentUser) {
      return;
    }

    this.dom.$importOptions.viewData = {
      context: viewData.context,
      editedCompanyId: viewData.editedCompanyId,
      hasStackedFormFields: viewData.hasStackedFormFields,
      shouldHidePartnerImport: viewData.isPartner,
    };

    if (!this.dom.$importOptions.started) {
      await this.dom.$importOptions.start();
    } else {
      await this.dom.$importOptions.controller.render();
    }

    this.importButtonSubscription = this.dom.$importOptions.formFilled$.subscribe(isFormFilled => {
      this.dom.$importButton.disabled = !isFormFilled;
    });

    this.setStoredImportedOption(viewData);
  }

  buildErrorView(error: Error) {
    this.removeLoadingState();
    GzToastNotificationWebComponent.buildToastWrapperElement({
      message: error.message,
      severity: ToastSeverity.ERROR,
    });
    toggleVisibility(this.dom.$wrapper, false);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  protected onDisconnect() {
    // Unsubscribe everything
    this.importButtonSubscription?.unsubscribe();
    this.formGroup?.unsubscribe();
  }

  private injectDependenciesInChildren(): void {
    this.dom.$importOptions.injectDependencies(this.dependencies);
    this.dom.$contactForm.injectDependencies(this.dependencies);
  }

  private setStoredImportedOption(viewData: ContactDetailsSectionViewData): void {
    if (viewData.importedCompanyId) {
      this.dom.$importOptions.setStoredFormGroupValue({ importedCompanyId: viewData.importedCompanyId });
      return;
    }

    if (viewData.importedAccountId) {
      this.dom.$importOptions.setStoredFormGroupValue({ importedAccountId: viewData.importedAccountId });
      return;
    }

    this.dom.$importOptions.setStoredFormGroupValue({});
  }

  private createFormGroup(): void {
    this.formGroup = new FormGroup({
      ...this.dom.$contactForm.getFormGroup().getFormChildren(),
      ...this.dom.$importOptions.getStoredFormGroup().getFormChildren(),
    });

    this.dom.$wrapper.dispatchEvent(
      new CustomEvent(MdrContactDetailsFormEvents.FORM_GROUP_CREATED, { bubbles: true, composed: true })
    );
  }
}
